import { useEffect, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { AskAiButton } from './askAiButton';

interface AiChatWindowProps {
  /** Called when the embedded chat-bot requests to close. */
  onClose: () => void;
  /** Iframe source. Defaults to the platform chat-bot embed route. */
  src?: string;
  /** Bottom offset (px) for the button shown while minimized. Default 30. */
  buttonBottom?: number;
  /** Right offset (px) for the button shown while minimized. Default 30. */
  buttonRight?: number;
}

const useStyles = makeStyles(() => ({
  '@keyframes slideUpFade': {
    from: { opacity: 0, transform: 'translateY(20px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
  window: {
    position: 'fixed',
    bottom: 0,
    right: 0,
    width: 500,
    maxWidth: '100vw',
    height: '100vh',
    backgroundColor: '#ffffff',
    boxShadow: '0 12px 48px rgba(0, 0, 0, 0.25)',
    overflow: 'hidden',
    zIndex: 9998,
    display: 'flex',
    flexDirection: 'column',
    animation: '$slideUpFade 0.3s ease-out',
  },
  iframeWrap: {
    width: '100%',
    flex: 1,
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  },
  iframe: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
    position: 'absolute',
    top: 0,
    left: 0,
  },
}));

/**
 * Fixed panel hosting the platform chat-bot in an iframe. Mirrors the
 * new_portal ChatbotWindow: it listens for the chat-bot's postMessage events
 * (close / minimize / maximize / redirect) and re-shows the launcher button
 * while minimized.
 */
export const AiChatWindow = ({
  onClose,
  src = '/chat-bot?embedded=true',
  buttonBottom = 30,
  buttonRight = 30,
}: AiChatWindowProps) => {
  const classes = useStyles();
  const [isMinimized, setIsMinimized] = useState(false);

  useEffect(() => {
    const handleMessage = (e: MessageEvent) => {
      const data = e?.data?.data;
      if (data === 'close-chat-bot') {
        onClose();
      } else if (data === 'redirect-chat-bot') {
        window.open(`/chat-bot?embedded=true&fullscreen=true`, '_blank');
      } else if (data === 'minimize-chat-bot') {
        setIsMinimized(true);
      } else if (data === 'maximize-chat-bot') {
        setIsMinimized(false);
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onClose]);

  return (
    <>
      <div
        className={classes.window}
        style={{ left: isMinimized ? '-9999px' : 'auto', right: isMinimized ? 'auto' : 0 }}
      >
        <div className={classes.iframeWrap}>
          <iframe src={src} className={classes.iframe} title="AI Assistant" />
        </div>
      </div>
      {isMinimized && (
        <AskAiButton onClick={() => setIsMinimized(false)} bottom={buttonBottom} right={buttonRight} />
      )}
    </>
  );
};

export default AiChatWindow;
